import { createBrowserRouter, Navigate } from 'react-router-dom';
import Routes from './Routes.ts';
import LoginPage from '@/pages/LoginPage.tsx';
import SandboxPage from '@/pages/SandboxPage.tsx';
import { transformRouterConfig } from '@/router/transformRouter.tsx';
import RegisterPage from '@/pages/RegisterPage.tsx';
import Default from '@/layouts/Default/Default.tsx';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage.tsx';
import ResetPasswordPage from '@/pages/ResetPasswordPage.tsx';
import SimpleCenter from '@/layouts/SimpleCenter/SimpleCenter.tsx';
import ConfirmEmailPage from '@/pages/ConfirmEmailPage.tsx';
import PaymentSelectPage from '@/pages/PaymentSelectPage.tsx';
import InputsPage from '@/pages/InputsPage.tsx';
import { LayoutInternal } from '$/views/emails/layout.tsx';
import AssessmentStep from '@/components/AssessmentSteps.tsx';
import ReportsTablePage from '@/pages/ReportsTablePage.tsx';
import AssessmentAnswersPage from '@/pages/AssessmentAnswersPage.tsx';
import AssessmentChart from '@/components/AssessmentCharts.tsx';
import AssessmentTablePage from '@/pages/AssessmentTablePage.tsx';
import ReportDasboardSummaryPage from '@/pages/ReportDasboardSummaryPage.tsx';
import TemplateTablePage from '@/pages/TemplateTablePage.tsx';
import TemplateViewer from '@/components/TemplateViewer.tsx';
import DomainRecommendationsPage from '@/pages/DomainRecommendationsPage.tsx';
import SubdomainRecommendationsPage from '@/pages/SubdomainRecommendationsPage.tsx';
import RecommendationsPage from '@/pages/RecommendationsPage.tsx';
import ProtectedRoute from '@/components/ProtectedRoute.tsx';
import DashboardRedirect from '@/components/DashboardRedirect.tsx';
import ResourcesPage from '@/pages/ResourcesPage.tsx';
import RequestsPage from '@/pages/RequestsPage';
import SuperAdminOrganizationsPage from '@/pages/SuperAdminOrganizationsPage';
import OrganizationsPage from '@/pages/OrganizationsPage';
import VerifyInvitePage from '@/pages/VerifyInvitePage';
import VerifyUsersPage from '@/pages/VerifyUsersPage';
import CompanyAdmin from '@/layouts/CompanyAdmin/CompanyAdmin.tsx';
import CompanyLoginPage from '@/pages/CompanyLoginPage.tsx';
import CompanyDashboardPage from '@/pages/CompanyDashboardPage.tsx';
import CompanyAdminsPage from '@/pages/CompanyAdminsPage.tsx';
import ChildReportsPage from '@/pages/ChildReportsPage.tsx';

const router = createBrowserRouter(
  transformRouterConfig([
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['loggedIn', 'forceResetPasswordCheck'],
      children: [
        {
          path: Routes.DASHBOARD,
          element: <DashboardRedirect />,
        },
        {
          path: Routes.SUPER_ADMIN_DASHBOARD,
          element: <ProtectedRoute allowedUserTypes={['superadmin']}>
            <SuperAdminOrganizationsPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.REQUESTS,
          element: <ProtectedRoute allowedUserTypes={['superadmin']}>
            <RequestsPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.SUPER_ADMIN_ORGANIZATIONS,
          element: <ProtectedRoute allowedUserTypes={['superadmin']}>
            <SuperAdminOrganizationsPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.ORGANIZATIONS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit']}>
            <OrganizationsPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.VERIFY_USERS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit']}>
            <VerifyUsersPage />
          </ProtectedRoute>,
        },
        {
          path: '/reports/summary/:id',
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <ReportDasboardSummaryPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.DOMAIN_RECOMMENDATIONS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <DomainRecommendationsPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.SUBDOMAIN_RECOMMENDATIONS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <SubdomainRecommendationsPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.RECOMMENDATIONS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <RecommendationsPage />
          </ProtectedRoute>,
        },
        
        {
          path: '/reports',
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <ReportsTablePage />
          </ProtectedRoute>,
        },
        {
          path: '/reports/summary',
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <ReportDasboardSummaryPage />
          </ProtectedRoute>,
        },
        {
          path: '/reports/summary/:id',
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <ReportDasboardSummaryPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.ASSESSMENT,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <AssessmentTablePage />
          </ProtectedRoute>,
        },
        {
          path: Routes.ASSESSMENT_STEP,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <AssessmentStep />
          </ProtectedRoute>,
        },
        {
          path: Routes.ASSESSMENT_RESULTS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <AssessmentChart />
          </ProtectedRoute>,
        },
        {
          path: Routes.ASSESSMENT_ANSWERS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit', 'child_user']}>
            <AssessmentAnswersPage />
          </ProtectedRoute>,
        },
        {
          path: Routes.TEMPLATES,
          element: <ProtectedRoute allowedUserTypes={['nonprofit']}>
            <TemplateTablePage />
          </ProtectedRoute>,
        },
        {
          path: Routes.TEMPLATE_VIEWER,
          element: <ProtectedRoute allowedUserTypes={['nonprofit']}>
            <TemplateViewer />
          </ProtectedRoute>
        },
        {
          path: Routes.RESOURCES,
          element: <ProtectedRoute allowedUserTypes={['nonprofit']}>
            <ResourcesPage />
          </ProtectedRoute>
        },
        {
          path: Routes.CHILD_REPORTS,
          element: <ProtectedRoute allowedUserTypes={['nonprofit']}>
            <ChildReportsPage />
          </ProtectedRoute>
        },
      
        {
          path: Routes.SANDBOX,
          element: <SandboxPage />,
        },
        {
          path: Routes.INPUTS,
          element: <InputsPage />,
        },
  
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['notLoggedIn'],
      children: [
        {
          path: Routes.FORGOT_PASSWORD,
          element: <ForgotPasswordPage />,
        },
        {
          path: Routes.LOGIN,
          element: <LoginPage />,
        },
        {
          path: Routes.REGISTER,
          element: <RegisterPage />,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <ConfirmEmailPage />,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.RESET_PASSWORD,
          element: <ResetPasswordPage />,
        },
      ],
    },
    {
      path: Routes.VERIFY_INVITE,
      element: <VerifyInvitePage />,
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.PAYMENT_SELECT,
          element: <PaymentSelectPage />,
        },
      ],
    },
    // Company Admin routes
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.COMPANY__LOGIN,
          element: <CompanyLoginPage />,
        },
      ],
    },
    {
      path: '/',
      element: <CompanyAdmin.Layout />,
      middleware: ['authCompanyAdmin', 'authCompanyVerified', 'authCompanyEnabled', 'forceResetPasswordCheck'],
      children: [
        {
          path: Routes.COMPANY__DASHBOARD,
          element: <CompanyDashboardPage />,
        },
        {
          path: Routes.COMPANY__ADMINS,
          element: <CompanyAdminsPage />,
        },
      ],
    },
    {
      path: Routes.EMAIL_TEMPLATE,
      element: (
        <LayoutInternal
          appUrl={import.meta.env.VITE_APP_URL}
          appName={'Viatelos'}
          logo={`${import.meta.env.VITE_APP_URL}/assets/logo.png`}
        >
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab animi atque possimus, quaerat quasi tempore
          totam! Architecto assumenda cumque deleniti dolor expedita facilis illo minima nihil quidem soluta? Hic,
          laboriosam!
        </LayoutInternal>
      ),
    },
    {
      path: '*',
      element: <Navigate to={Routes.DASHBOARD} />,
    },
  ])
);

export default router;